import { useRef, useState } from "react";
import { toast } from "react-toastify";

export type RangeFilter = { min?: string; max?: string };
export type EnrichmentStatus = { not_enriched?: boolean; not_found?: boolean };
export type TraderManufacturerFilter = { values?: string[] };
export type PresenceStatus = { present?: boolean; not_found?: boolean; not_enriched?: boolean };

export type WorkspaceFiltersValue = {
  shipments?: RangeFilter;
  weight?: RangeFilter;
  shipment_value?: RangeFilter;
  trading_partners?: RangeFilter;
  trading_countries?: RangeFilter;
  revenue?: RangeFilter & EnrichmentStatus;
  employee_count?: RangeFilter & EnrichmentStatus;
  domain?: PresenceStatus;
  description?: PresenceStatus;
  pricing_agent?: RangeFilter & EnrichmentStatus;
  enriched_contact?: PresenceStatus;
  trader_manufacturer?: TraderManufacturerFilter;
};

export type FilterKey = keyof WorkspaceFiltersValue;

type FilterType = "range" | "range_enrichment" | "presence" | "trader_manufacturer";

const TRADER_MANUFACTURER_OPTIONS = ["Trader", "Manufacturer", "Both", "Unable to determine"];

type FilterDef = {
  key: FilterKey;
  label: string;
  type: FilterType;
  icon: string;
};

const FILTER_DEFS: FilterDef[] = [
  { key: "shipments", label: "Shipments", type: "range", icon: "local_shipping" },
  { key: "trading_partners", label: "No of Trading Partners", type: "range", icon: "handshake" },
  { key: "trading_countries", label: "Trading Countries", type: "range", icon: "public" },
  { key: "weight", label: "Weight", type: "range", icon: "scale" },
  { key: "shipment_value", label: "Shipment Value (USD)", type: "range", icon: "attach_money" },
  { key: "revenue", label: "Revenue (USD Millions)", type: "range_enrichment", icon: "currency_exchange" },
  { key: "employee_count", label: "Employee Count", type: "range_enrichment", icon: "people" },
  { key: "domain", label: "Domain", type: "presence", icon: "language" },
  { key: "description", label: "Description", type: "presence", icon: "description" },
  { key: "pricing_agent", label: "Pricing Agent (Average)", type: "range_enrichment", icon: "price_check" },
  { key: "enriched_contact", label: "Enriched Contact", type: "presence", icon: "contact_mail" },
  { key: "trader_manufacturer", label: "Trader / Manufacturer", type: "trader_manufacturer", icon: "factory" },
];

export const isFilterActive = (value: WorkspaceFiltersValue[FilterKey]): boolean => {
  if (!value) return false;
  return Object.values(value).some((v) => (Array.isArray(v) ? v.length > 0 : v !== undefined && v !== "" && v !== false));
};

export const countActiveFilters = (filters: WorkspaceFiltersValue): number => Object.values(filters).filter((v) => isFilterActive(v as WorkspaceFiltersValue[FilterKey])).length;

interface WorkspaceFiltersPanelProps {
  appliedFilters: WorkspaceFiltersValue;
  onApplyFilter: (key: FilterKey, value: WorkspaceFiltersValue[FilterKey]) => void;
}

const inputClass = "tw-w-full tw-rounded-md tw-border tw-border-gray-300 tw-px-2.5 tw-py-1.5 tw-text-sm tw-text-gray-900 tw-outline-none focus:tw-border-primary-purple-500 focus:tw-ring-1 focus:tw-ring-primary-purple-500";

const checkboxClass = "tw-h-4 tw-w-4 tw-flex-shrink-0 tw-rounded tw-border-gray-300 tw-accent-primary-purple-500 tw-cursor-pointer after:tw-hidden";

const WorkspaceFiltersPanel = ({ appliedFilters, onApplyFilter }: WorkspaceFiltersPanelProps) => {
  const [activeKey, setActiveKey] = useState<FilterKey | null>(null);
  const [draft, setDraft] = useState<WorkspaceFiltersValue>({});
  const [subPanelTop, setSubPanelTop] = useState(0);
  const mainPanelRef = useRef<HTMLDivElement>(null);

  const handleSelectFilter = (key: FilterKey, btn: HTMLButtonElement) => {
    const panelTop = mainPanelRef.current?.getBoundingClientRect().top ?? 0;
    const btnTop = btn.getBoundingClientRect().top;
    setSubPanelTop(btnTop - panelTop);
    setActiveKey(key);
    setDraft({ ...appliedFilters });
  };

  const handleApply = () => {
    if (!activeKey) return;
    const val = draft[activeKey] as RangeFilter | undefined;
    if (val?.min !== undefined && val?.max !== undefined && val.min !== "" && val.max !== "" && Number(val.min) > Number(val.max)) {
      toast.error("'From' value cannot be greater than 'To' value.");
      return;
    }
    const isEmpty = !isFilterActive(draft[activeKey]);
    onApplyFilter(activeKey, isEmpty ? undefined : draft[activeKey]);
    setActiveKey(null);
  };

  const updateRange = (key: FilterKey, field: "min" | "max", value: string) => {
    const sanitized = value.replace(/\D/g, "").replace(/^0+(\d)/, "$1");
    setDraft((prev) => ({ ...prev, [key]: { ...((prev[key] as object) || {}), [field]: sanitized } }));
  };

  const updateCheckbox = (key: FilterKey, field: string, value: boolean) => {
    setDraft((prev) => ({ ...prev, [key]: { ...((prev[key] as object) || {}), [field]: value } }));
  };

  const toggleTraderManufacturerValue = (value: string, checked: boolean) => {
    setDraft((prev) => {
      const current = prev.trader_manufacturer?.values ?? [];
      const values = checked ? [...current, value] : current.filter((v) => v !== value);
      return { ...prev, trader_manufacturer: { ...prev.trader_manufacturer, values } };
    });
  };

  const activeDef = FILTER_DEFS.find((d) => d.key === activeKey) ?? null;

  const renderSubPanel = () => {
    if (!activeDef || !activeKey) return null;

    if (activeDef.type === "range" || activeDef.type === "range_enrichment") {
      const val = (draft[activeKey] || {}) as RangeFilter & EnrichmentStatus;
      return (
        <div className="tw-flex tw-flex-col tw-gap-4 tw-p-4 tw-w-[220px]">
          <span className="tw-text-sm tw-font-semibold tw-text-gray-900">{activeDef.label}</span>
          <div className="tw-flex tw-items-end tw-gap-2">
            <div className="tw-flex tw-flex-col tw-gap-1 tw-flex-1">
              <label className="tw-text-xs tw-text-gray-500">from</label>
              <input type="text" inputMode="numeric" value={val.min ?? ""} onChange={(e) => updateRange(activeKey, "min", e.target.value)} className={inputClass} placeholder="Min" />
            </div>
            <span className="tw-text-gray-400 tw-pb-2 tw-text-sm">-</span>
            <div className="tw-flex tw-flex-col tw-gap-1 tw-flex-1">
              <label className="tw-text-xs tw-text-gray-500">to</label>
              <input type="text" inputMode="numeric" value={val.max ?? ""} onChange={(e) => updateRange(activeKey, "max", e.target.value)} className={inputClass} placeholder="Max" />
            </div>
          </div>
          {activeDef.type === "range_enrichment" && (
            <div className="tw-flex tw-flex-col tw-gap-2">
              <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                <input type="checkbox" checked={val.not_enriched ?? false} onChange={(e) => updateCheckbox(activeKey, "not_enriched", e.target.checked)} className={checkboxClass} />
                <span className="tw-text-sm tw-text-gray-700">Not enriched</span>
              </label>
              <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                <input type="checkbox" checked={val.not_found ?? false} onChange={(e) => updateCheckbox(activeKey, "not_found", e.target.checked)} className={checkboxClass} />
                <span className="tw-text-sm tw-text-gray-700">Not found</span>
              </label>
            </div>
          )}
          <button type="button" className="tw-w-full tw-rounded-lg tw-bg-primary-purple-500 tw-text-white tw-text-sm tw-font-medium tw-py-2 hover:tw-bg-primary-purple-600 tw-border-0" onClick={handleApply}>
            Apply
          </button>
        </div>
      );
    }

    if (activeDef.type === "trader_manufacturer") {
      const val = draft.trader_manufacturer || {};
      return (
        <div className="tw-flex tw-flex-col tw-gap-4 tw-p-4 tw-w-[220px]">
          <span className="tw-text-sm tw-font-semibold tw-text-gray-900">{activeDef.label}</span>
          <div className="tw-flex tw-flex-col tw-gap-2">
            {TRADER_MANUFACTURER_OPTIONS.map((option) => (
              <label key={option} className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                <input type="checkbox" checked={val.values?.includes(option) ?? false} onChange={(e) => toggleTraderManufacturerValue(option, e.target.checked)} className={checkboxClass} />
                <span className="tw-text-sm tw-text-gray-700">{option}</span>
              </label>
            ))}
          </div>
          <button type="button" className="tw-w-full tw-rounded-lg tw-bg-primary-purple-500 tw-text-white tw-text-sm tw-font-medium tw-py-2 hover:tw-bg-primary-purple-600 tw-border-0" onClick={handleApply}>
            Apply
          </button>
        </div>
      );
    }

    if (activeDef.type === "presence") {
      const val = (draft[activeKey] || {}) as PresenceStatus;
      return (
        <div className="tw-flex tw-flex-col tw-gap-4 tw-p-4 tw-w-[220px]">
          <span className="tw-text-sm tw-font-semibold tw-text-gray-900">{activeDef.label}</span>
          <div className="tw-flex tw-flex-col tw-gap-2">
            <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
              <input type="checkbox" checked={val.present ?? false} onChange={(e) => updateCheckbox(activeKey, "present", e.target.checked)} className={checkboxClass} />
              <span className="tw-text-sm tw-text-gray-700">Present</span>
            </label>
            <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
              <input type="checkbox" checked={val.not_found ?? false} onChange={(e) => updateCheckbox(activeKey, "not_found", e.target.checked)} className={checkboxClass} />
              <span className="tw-text-sm tw-text-gray-700">Not found</span>
            </label>
            <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
              <input type="checkbox" checked={val.not_enriched ?? false} onChange={(e) => updateCheckbox(activeKey, "not_enriched", e.target.checked)} className={checkboxClass} />
              <span className="tw-text-sm tw-text-gray-700">Not enriched</span>
            </label>
          </div>
          <button type="button" className="tw-w-full tw-rounded-lg tw-bg-primary-purple-500 tw-text-white tw-text-sm tw-font-medium tw-py-2 hover:tw-bg-primary-purple-600 tw-border-0" onClick={handleApply}>
            Apply
          </button>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="tw-relative">
      {/* Sub panel — absolutely positioned to the left of the main panel */}
      {activeKey && (
        <div style={{ top: subPanelTop }} className="tw-absolute tw-right-[calc(100%+8px)] tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white tw-shadow-lg tw-overflow-hidden">
          {renderSubPanel()}
        </div>
      )}

      {/* Main panel — filter categories */}
      <div ref={mainPanelRef} className="tw-flex tw-flex-col tw-py-1 tw-min-w-[220px] tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white tw-shadow-lg">
        {FILTER_DEFS.map((def) => {
          const active = isFilterActive(appliedFilters[def.key]);
          const selected = activeKey === def.key;
          return (
            <button key={def.key} type="button" className={`tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2.5 tw-text-sm tw-border-0 tw-bg-transparent tw-w-full tw-text-left ${selected ? "tw-bg-primary-purple-50 tw-text-primary-purple-700" : "tw-text-gray-700 hover:tw-bg-gray-50"}`} onClick={(e) => handleSelectFilter(def.key, e.currentTarget)}>
              <span className={`material-symbols-outlined tw-text-[18px] tw-leading-none ${selected ? "tw-text-primary-purple-500" : "tw-text-gray-500"}`}>{def.icon}</span>
              <span className="tw-flex-1 tw-truncate">{def.label}</span>
              {active && (
                <span
                  role="button"
                  className="tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-4 tw-rounded-full tw-bg-primary-purple-500 tw-text-white tw-flex-shrink-0 tw-cursor-pointer hover:tw-bg-primary-purple-600 tw-p-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    onApplyFilter(def.key, undefined);
                    setActiveKey(null);
                  }}>
                  <span className="material-symbols-outlined tw-leading-none">close</span>
                </span>
              )}
              <span className={`material-symbols-outlined tw-text-[18px] tw-leading-none ${selected ? "tw-text-primary-purple-400" : "tw-text-gray-400"}`}>chevron_left</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default WorkspaceFiltersPanel;
